// ============================================================
// 📦 BackupButton.jsx  — 프로젝트 백업 다운로드 버튼 컴포넌트
// ------------------------------------------------------------
// 역할  : 버튼 하나를 클릭하면 /api/backup 을 호출해
//         ZIP 파일을 브라우저 다운로드로 받아줍니다.
//
// 사용법: App.js 의 "설정(settings)" 탭 안에 아래처럼 추가하세요.
//
//   import BackupButton from './components/BackupButton';
//   ...
//   {tab === "settings" && (
//     <div>
//       ...
//       <BackupButton />   ← 원하는 위치에 삽입
//     </div>
//   )}
// ============================================================

import React, { useState } from 'react';

export default function BackupButton() {

  // ─── 상태 ───────────────────────────────────────────────────
  // 'idle'      : 기본 상태 (버튼 클릭 대기)
  // 'loading'   : 서버에서 ZIP 생성 중 (스피너 표시)
  // 'done'      : 다운로드 완료
  // 'error'     : 오류 발생
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');   // 오류 메시지

  // ─── 백업 실행 함수 ─────────────────────────────────────────
  const handleBackup = async () => {
    setStatus('loading');
    setErrorMsg('');

    try {
      // 1) /api/backup 에 GET 요청
      //    → 서버가 ZIP 을 만들어 바이너리(Blob)로 응답
      const response = await fetch('/api/backup');

      if (!response.ok) {
        // HTTP 오류(400, 500 등)
        throw new Error(`서버 오류: ${response.status}`);
      }

      // 2) 응답 바디를 Blob(이진 데이터 덩어리)으로 읽기
      const blob = await response.blob();

      // 3) Blob → 가짜 다운로드 링크 만들기
      //    브라우저가 "저장하기" 대화상자를 띄우는 트릭
      const url  = URL.createObjectURL(blob);   // 임시 URL 생성
      const link = document.createElement('a'); // 보이지 않는 <a> 태그 생성

      // 4) 파일 이름 결정
      //    서버 헤더에 파일명이 있으면 그걸 사용, 없으면 오늘 날짜로 생성
      const disposition = response.headers.get('Content-Disposition') || '';
      const nameMatch   = disposition.match(/filename="?([^"]+)"?/);
      const today       = new Date().toISOString().slice(0, 10);
      link.download     = nameMatch ? nameMatch[1] : `backup-${today}.zip`;

      link.href = url;
      document.body.appendChild(link);
      link.click();               // 클릭 → 다운로드 시작
      document.body.removeChild(link);
      URL.revokeObjectURL(url);  // 임시 URL 메모리 해제

      setStatus('done');

      // 3초 후 버튼 상태를 다시 'idle' 로 복귀
      setTimeout(() => setStatus('idle'), 3000);

    } catch (err) {
      console.error('[BackupButton] 다운로드 오류:', err);
      setErrorMsg(err.message || '알 수 없는 오류가 발생했습니다.');
      setStatus('error');
    }
  };

  // ─── 상태별 버튼 레이블 / 색상 ─────────────────────────────
  const statusConfig = {
    idle:    { label: '💾 프로젝트 백업 다운로드', bg: '#2b6cb0', cursor: 'pointer'    },
    loading: { label: '⏳ ZIP 생성 중… 잠시만요', bg: '#718096', cursor: 'wait'       },
    done:    { label: '✅ 다운로드 완료!',         bg: '#276749', cursor: 'default'    },
    error:   { label: '❌ 오류 발생 (재시도)',      bg: '#c53030', cursor: 'pointer'    },
  };
  const cfg = statusConfig[status];

  // ─── 렌더 ──────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

      {/* 설명 문구 */}
      <p style={{ fontSize: '13px', color: '#4a5568', margin: 0 }}>
        node_modules, .git, dist, build 등 시스템 폴더를 제외한<br />
        모든 소스 파일을 ZIP 으로 압축해 다운로드합니다.
      </p>

      {/* 백업 버튼 */}
      <button
        type="button"
        disabled={status === 'loading'}   // 생성 중에는 중복 클릭 방지
        onClick={handleBackup}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: '10px 20px',
          fontSize: '14px',
          fontWeight: '700',
          color: '#fff',
          backgroundColor: cfg.bg,
          border: 'none',
          borderRadius: '6px',
          cursor: cfg.cursor,
          transition: 'background-color 0.2s',
          alignSelf: 'flex-start',  // 버튼이 부모 너비를 꽉 채우지 않도록
        }}
      >
        {/* 로딩 중에만 회전 스피너 표시 */}
        {status === 'loading' && (
          <>
            <style>{`
              @keyframes spin-backup {
                to { transform: rotate(360deg); }
              }
              .backup-spinner {
                width: 16px; height: 16px;
                border: 3px solid rgba(255,255,255,0.4);
                border-top-color: #fff;
                border-radius: 50%;
                animation: spin-backup 0.8s linear infinite;
                flex-shrink: 0;
              }
            `}</style>
            <span className="backup-spinner" />
          </>
        )}
        {cfg.label}
      </button>

      {/* 오류 메시지 */}
      {status === 'error' && errorMsg && (
        <p style={{
          fontSize: '12px',
          color: '#c53030',
          margin: 0,
          padding: '8px 12px',
          background: '#fff5f5',
          border: '1px solid #fed7d7',
          borderRadius: '4px',
        }}>
          ⚠️ {errorMsg}
        </p>
      )}
    </div>
  );
}
